<template>
  <div class="cws-container blog-wrapper">
    <div>
      <b-row>
        <b-col md="12" lg="12">
          <b-card
              title="Проверка Пакета"
              class="mb-3"
              border-variant="primary"
          >
            <b-card-text>
              Загрузка SIQ-файла для проверки корректности пакета
            </b-card-text>

            <b-form-group
                label="SIQ-Файл"
                label-for="siq-file"
            >
              <b-button
                  variant="outline-primary"
                  @click="$refs.refInputUploadFile.click()"
              >
                <input
                    ref="refInputUploadFile"
                    type="file"
                    class="d-none"
                    id="siq-file"
                    @input="inputSiqFileRenderer"
                />
                <span class="d-none d-sm-inline">Upload</span>
                <feather-icon
                    icon="UploadIcon"
                    class="d-inline d-sm-none"
                />
              </b-button>
            </b-form-group>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- content -->
    <div class="blog-detail-wrapper">
      <b-row v-if="validationData">
        <b-col cols="12">
          <b-overlay
              :show="isLoading"
              rounded="sm"
          >
            <b-card>
              <h2 class="mb-1">{{ validationData.title }}</h2>
              <p class="text-muted mb-2">
                Автор: <strong>{{ validationData.author }}</strong> |
                Раундов: <strong>{{ roundsCount }}</strong> |
                Вопросов: <strong>{{ itemsCount }}</strong>
              </p>

              <!-- Общие параметры пакета -->
              <b-row v-if="summaryStats.length" class="mb-2">
                <b-col
                    v-for="stat in summaryStats"
                    :key="stat.label"
                    md="3"
                    sm="6"
                    cols="12"
                    class="mb-1"
                >
                  <div class="border rounded p-1 text-center h-100">
                    <div class="text-muted small">{{ stat.label }}</div>
                    <!-- Пока нейтрально-зелёное, без порогов; место под будущие ворнинги -->
                    <div class="text-success h4 mb-0">{{ stat.value }}</div>
                  </div>
                </b-col>
              </b-row>
            </b-card>

            <!-- Формат стоимостей -->
            <b-card v-if="validationData.cost_format" title="Формат стоимостей" class="mt-2">
              <template v-if="!showCostFormat">
                <b-card-text>Схема стоимостей и проблемы по темам</b-card-text>
                <b-button variant="primary" @click="showCostFormat = true">Проверить</b-button>
              </template>
              <div v-else>
                <h5 class="mb-1">
                  Схема:
                  <b-badge variant="light-primary">{{ validationData.cost_format.scheme_label }}</b-badge>
                </h5>
                <b-table-simple responsive bordered small>
                  <b-thead>
                    <b-tr>
                      <b-th>Тема</b-th>
                      <b-th>Стоимости</b-th>
                      <b-th>Проблема</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody>
                    <template v-for="(round, ri) in validationData.cost_format.rounds">
                      <b-tr :key="`cr-${ri}`" variant="light">
                        <b-th colspan="3">
                          {{ round.name }}
                          <b-badge :variant="roundCategoryVariant(round.category)">{{ roundCategoryLabel(round.category) }}</b-badge>
                        </b-th>
                      </b-tr>
                      <b-tr
                          v-for="(theme, ti) in round.themes"
                          :key="`cr-${ri}-t-${ti}`"
                          :variant="costRowVariant(theme.status)"
                      >
                        <b-td>{{ theme.name }}</b-td>
                        <b-td>{{ theme.prices.join('-') || '—' }}</b-td>
                        <b-td class="small">{{ theme.message }}</b-td>
                      </b-tr>
                    </template>
                  </b-tbody>
                </b-table-simple>
              </div>
            </b-card>

            <!-- Исполнители и файлы -->
            <b-card v-if="validationData.items" title="Исполнители и файлы" class="mt-2">
              <template v-if="!showArtistsCheck">
                <b-card-text>Проверка исполнителей по БД и Spotify, контента вопросов</b-card-text>
                <b-button variant="primary" @click="runArtistsCheck">Проверить</b-button>
              </template>
              <b-table-simple v-else responsive bordered small>
                <b-thead>
                  <b-tr>
                    <b-th>№</b-th>
                    <b-th>Артист (файл)</b-th>
                    <b-th>Артист (Spotify)</b-th>
                    <b-th>Трек</b-th>
                    <b-th>Контент</b-th>
                  </b-tr>
                </b-thead>
                <b-tbody>
                  <b-tr
                      v-for="(item, itemIndex) in validationData.items"
                      :key="itemIndex"
                      :variant="rowIsRed(item) ? 'danger' : null"
                  >
                    <b-td>
                      {{ item.round_index + 1 }}.{{ item.theme_index + 1 }}.{{ item.question_index }}
                      <b-badge v-if="item.special_type === 'auction'" variant="light-info">аукцион</b-badge>
                      <b-badge v-if="item.special_type === 'cat'" variant="light-info">кот</b-badge>
                    </b-td>

                    <!-- Артист (файл) -->
                    <b-td>
                      <div v-for="(artist, artistIndex) in item.artists" :key="artistIndex">
                        <b-link
                            v-if="artist.db_id"
                            :to="{ name: 'artist-view', params: { id: artist.db_id } }"
                            :class="artistClass(artist)"
                        >
                          {{ artist.name }}
                        </b-link>
                        <span v-else :class="artistClass(artist)">{{ artist.name }}</span>
                        <feather-icon
                            v-if="artistState(artist) === 'ok'"
                            icon="CheckIcon"
                            size="14"
                            class="text-success"
                        />
                        <!-- Регистр отличается — убираемый бэдж -->
                        <template v-if="showCaseMismatch(artist, itemIndex, artistIndex)">
                          <b-badge variant="light-secondary" title="Регистр отличается">Аа</b-badge>
                          <b-button
                              size="sm"
                              variant="flat-success"
                              class="p-25"
                              @click="dismissCaseMismatch(artistKey(itemIndex, artistIndex))"
                          >
                            <feather-icon icon="CheckIcon" size="14" />
                          </b-button>
                        </template>
                        <!-- В БД есть привязка к Spotify — заметка, убираемая -->
                        <template v-if="showNote(artist, itemIndex, artistIndex)">
                          <b-badge variant="light-info" title="В БД есть привязка к Spotify">
                            <feather-icon icon="FileTextIcon" size="12" />
                          </b-badge>
                          <b-button
                              size="sm"
                              variant="flat-success"
                              class="p-25"
                              @click="dismissNote(artistKey(itemIndex, artistIndex))"
                          >
                            <feather-icon icon="CheckIcon" size="14" />
                          </b-button>
                        </template>
                        <b-button
                            v-if="artistState(artist) === 'warning' && !dismissedArtists.has(artistKey(itemIndex, artistIndex))"
                            size="sm"
                            variant="flat-success"
                            class="p-25"
                            @click="dismissArtist(artistKey(itemIndex, artistIndex))"
                        >
                          <feather-icon icon="CheckIcon" size="14" />
                        </b-button>
                      </div>
                      <span v-if="!item.artists.length" class="text-danger">— нет артиста —</span>
                    </b-td>

                    <!-- Артист (Spotify) -->
                    <b-td>
                      <div v-for="(artist, artistIndex) in item.artists" :key="artistIndex">
                        <b-spinner v-if="artistState(artist) === 'loading'" small />
                        <template v-else-if="artistSpotify(artist) && artistSpotify(artist).spotify">
                          {{ artistSpotify(artist).spotify.name }}
                          <b-link
                              :href="spotifyArtistUrl(artistSpotify(artist).spotify.id)"
                              target="_blank"
                              rel="noopener"
                              title="Открыть в Spotify (популярность)"
                          >
                            <b-badge variant="light-primary">{{ artistSpotify(artist).spotify.popularity }}</b-badge>
                          </b-link>
                        </template>
                        <template v-else-if="artistSpotify(artist) && artistSpotify(artist).candidates && artistSpotify(artist).candidates.length">
                          <div
                              v-for="cand in artistSpotify(artist).candidates"
                              :key="cand.id"
                              class="small text-muted"
                          >
                            {{ cand.name }}
                            <b-link
                                :href="spotifyArtistUrl(cand.id)"
                                target="_blank"
                                rel="noopener"
                                title="Открыть в Spotify (популярность)"
                            >
                              <b-badge variant="light-secondary">{{ cand.popularity }}</b-badge>
                            </b-link>
                          </div>
                        </template>
                        <span v-else class="text-danger small">—</span>
                      </div>
                    </b-td>

                    <!-- Трек -->
                    <b-td>
                      <div v-for="(row, rowIndex) in trackRows(item)" :key="rowIndex">
                        {{ row.track }}
                        <template v-if="row.artist">
                          <b-spinner v-if="trackState(row) === 'loading'" small />
                          <b-link
                              v-else-if="trackState(row) === 'ok'"
                              :href="spotifyTrackUrl(trackSpotify(row).spotify.id)"
                              target="_blank"
                              rel="noopener"
                              title="Есть в Spotify — открыть трек"
                          >
                            <feather-icon icon="CheckIcon" size="14" class="text-success" />
                          </b-link>
                          <template v-else-if="trackState(row) === 'bad'">
                            <feather-icon
                                icon="XIcon"
                                size="14"
                                class="text-danger"
                                title="У этого исполнителя в Spotify не найдена"
                            />
                            <div
                                v-for="cand in trackSpotify(row).candidates"
                                :key="cand.id"
                                class="small text-muted"
                            >
                              <b-link :href="spotifyTrackUrl(cand.id)" target="_blank" rel="noopener">
                                {{ cand.name }}
                              </b-link>
                              — {{ cand.artists }}
                            </div>
                          </template>
                        </template>
                      </div>
                    </b-td>

                    <!-- Контент: все файлы вопроса, ворнинги по каждому -->
                    <b-td>
                      <div v-for="(content, fileIndex) in item.contents" :key="fileIndex">
                        <span :class="fileClass(content, itemIndex, fileIndex)">
                          {{ contentIcon(content.kind) }}
                          <template v-if="content.size_bytes">{{ Math.round(content.size_bytes / 1024) }} КБ</template>
                        </span>
                        <b-button
                            v-if="fileHasWarning(content, itemIndex, fileIndex)"
                            size="sm"
                            variant="flat-success"
                            class="p-25"
                            @click="dismissContent(contentKey(itemIndex, fileIndex))"
                        >
                          <feather-icon icon="CheckIcon" size="14" />
                        </b-button>
                        <!-- Причины ворнинга — под красной подсветкой -->
                        <div v-if="fileHasWarning(content, itemIndex, fileIndex)" class="small text-danger mt-25">
                          <div v-for="reason in contentWarningReasons(content)" :key="reason">{{ reason }}</div>
                        </div>
                      </div>
                    </b-td>
                  </b-tr>
                </b-tbody>
              </b-table-simple>
            </b-card>

            <!-- Анализ результатов: статистика сыгранных игр -->
            <b-card title="Анализ результатов" class="mt-2">
              <b-card-text>
                Статистика сыгранных игр по названию и автору пакета (vladimirkhil.com)
              </b-card-text>

              <b-button
                  variant="primary"
                  :disabled="statsLoading"
                  @click="loadPackageStats"
              >
                <b-spinner v-if="statsLoading" small class="mr-50" />
                Проверить
              </b-button>

              <template v-if="statsData">
                <p class="mt-2 mb-1">
                  Игр начато: <strong>{{ (statsData.topLevelStats || {}).startedGameCount || 0 }}</strong> |
                  завершено: <strong>{{ (statsData.topLevelStats || {}).completedGameCount || 0 }}</strong>
                </p>

                <p v-if="!statsHasData" class="text-muted mb-0">Статистика по пакету не найдена</p>

                <b-table-simple v-else responsive bordered small>
                  <b-tbody>
                    <template v-for="(round, roundIndex) in validationData.rounds">
                      <b-tr :key="`st-${roundIndex}`" variant="light">
                        <b-th :colspan="maxQuestionsInRound(round) + 1">{{ round.name }}</b-th>
                      </b-tr>
                      <b-tr
                          v-for="(theme, themeIndex) in round.themes"
                          :key="`st-${roundIndex}-t-${themeIndex}`"
                      >
                        <b-td class="font-weight-bold">{{ theme.name }}</b-td>
                        <b-td
                            v-for="(price, questionIndex) in theme.prices"
                            :key="`st-${roundIndex}-t-${themeIndex}-q-${questionIndex}`"
                            :class="specialCellClass(roundIndex, themeIndex, questionIndex)"
                            :style="statCellStyle(round, roundIndex, theme, themeIndex, questionIndex)"
                            v-b-tooltip.hover
                            :title="specialCellTitle(roundIndex, themeIndex, questionIndex)"
                        >
                          <div class="d-flex align-items-center justify-content-between">
                            <div
                                v-if="statFor(roundIndex, theme, themeIndex, questionIndex) && statFor(roundIndex, theme, themeIndex, questionIndex).shownCount"
                                class="small text-nowrap"
                            >
                              <span v-b-tooltip.hover class="mr-25" title="Показов вопроса всего">
                                <feather-icon icon="EyeIcon" size="12" />{{ statFor(roundIndex, theme, themeIndex, questionIndex).shownCount }}
                              </span>
                              <span v-b-tooltip.hover class="mr-25" title="Попыток ответить, %">
                                <feather-icon icon="MousePointerIcon" size="12" />{{ takenPercent(statFor(roundIndex, theme, themeIndex, questionIndex)) }}%
                              </span>
                              <span
                                  v-if="!statFor(roundIndex, theme, themeIndex, questionIndex).answeredCount"
                                  v-b-tooltip.hover
                                  class="mr-25"
                                  title="Вопрос ни разу не взят"
                              >
                                <feather-icon icon="AlertTriangleIcon" size="12" class="text-danger" />
                              </span>
                              <span v-b-tooltip.hover class="mr-25" title="Правильных ответов">
                                <feather-icon icon="CheckIcon" size="12" class="text-success" />{{ correctPercent(statFor(roundIndex, theme, themeIndex, questionIndex)) }}%
                              </span>
                              <span
                                  v-if="statFor(roundIndex, theme, themeIndex, questionIndex).wrongCount"
                                  v-b-tooltip.hover
                                  class="mr-25"
                                  title="Неверных ответов"
                              >
                                <feather-icon icon="XIcon" size="12" class="text-danger" />{{ statFor(roundIndex, theme, themeIndex, questionIndex).wrongCount }}
                              </span>
                            </div>
                            <div v-else class="small text-muted">—</div>
                            <div class="h5 font-weight-bold mb-0 ml-50">{{ price }}</div>
                          </div>
                          <div v-if="answerFor(roundIndex, themeIndex, questionIndex)" class="small text-muted">
                            <template v-if="answerFor(roundIndex, themeIndex, questionIndex).length <= 30">{{ answerFor(roundIndex, themeIndex, questionIndex) }}</template>
                            <feather-icon
                                v-else
                                v-b-tooltip.hover
                                :title="answerFor(roundIndex, themeIndex, questionIndex)"
                                icon="HelpCircleIcon"
                                size="12"
                            />
                          </div>
                        </b-td>
                      </b-tr>
                    </template>
                  </b-tbody>
                </b-table-simple>
              </template>
            </b-card>

            <!-- Проверка сложности по БД -->
            <b-card title="Проверка сложности по БД" class="mt-2">
              <b-card-text>
                Сложность вопросов по формуле генератора (артист + трек, шкала 1–10, 10 — сложный)
                с подсветкой отклонения от целевой сложности: плоские темы — 5, возрастающие — 3→7,
                с поправкой на сложность пакета
              </b-card-text>

              <div class="d-flex align-items-end">
                <b-button
                    variant="primary"
                    :disabled="difficultyLoading"
                    @click="loadDifficulty"
                >
                  <b-spinner v-if="difficultyLoading" small class="mr-50" />
                  Проверить
                </b-button>
                <b-form-group
                    label="Сложность пакета"
                    label-for="pack-difficulty"
                    class="ml-1 mb-0"
                >
                  <b-form-spinbutton
                      id="pack-difficulty"
                      v-model="packDifficulty"
                      vertical
                      min="1"
                      max="10"
                      step="1"
                  />
                </b-form-group>
              </div>

              <!-- Саммари подсветки: стековая полоска на всю ширину, сегменты по цветам -->
              <div
                  v-if="difficultyData && difficultySummary.total"
                  class="d-flex w-100 rounded overflow-hidden mt-2 difficulty-summary-bar"
              >
                <div
                    v-for="segment in difficultySummary.segments"
                    :key="segment.variant"
                    :class="`bg-${segment.variant}`"
                    :style="{ width: segment.width + '%' }"
                    class="text-center text-white small py-25 text-nowrap overflow-hidden"
                >
                  {{ segment.percent }}% ({{ segment.count }})
                </div>
              </div>

              <b-table-simple v-if="difficultyData" responsive bordered small class="mt-2">
                <b-tbody>
                  <template v-for="(round, roundIndex) in validationData.rounds">
                    <b-tr :key="`df-${roundIndex}`" variant="light">
                      <b-th :colspan="maxQuestionsInRound(round) + 1">{{ round.name }}</b-th>
                    </b-tr>
                    <b-tr
                        v-for="(theme, themeIndex) in round.themes"
                        :key="`df-${roundIndex}-t-${themeIndex}`"
                    >
                      <b-td class="font-weight-bold">{{ theme.name }}</b-td>
                      <b-td
                          v-for="(price, questionIndex) in theme.prices"
                          :key="`df-${roundIndex}-t-${themeIndex}-q-${questionIndex}`"
                          :variant="difficultyCellVariant(round, roundIndex, theme, themeIndex, questionIndex)"
                          :class="specialCellClass(roundIndex, themeIndex, questionIndex)"
                          v-b-tooltip.hover
                          :title="specialCellTitle(roundIndex, themeIndex, questionIndex)"
                      >
                        <div class="d-flex justify-content-between align-items-start">
                          <div class="font-weight-bold">{{ price }}</div>
                          <div class="text-nowrap">
                            <b-link
                                v-for="artistId in ((difficultyFor(roundIndex, themeIndex, questionIndex) || {}).artist_ids || [])"
                                :key="`a-${artistId}`"
                                :to="{ name: 'artist-view', params: { id: artistId } }"
                                target="_blank"
                                class="mr-25"
                                title="Артист в БД"
                            >
                              <feather-icon icon="UserIcon" size="12" />
                            </b-link>
                            <b-link
                                v-for="trackId in entryTrackIds(difficultyFor(roundIndex, themeIndex, questionIndex))"
                                :key="`t-${trackId}`"
                                :to="{ name: 'track-detail', params: { id: trackId } }"
                                target="_blank"
                                class="mr-25"
                                title="Трек в БД"
                            >
                              <feather-icon icon="MusicIcon" size="12" />
                            </b-link>
                          </div>
                        </div>
                        <div class="small text-nowrap text-center">
                          <span
                              v-if="difficultyFor(roundIndex, themeIndex, questionIndex) && difficultyFor(roundIndex, themeIndex, questionIndex).difficulty !== null"
                              v-b-tooltip.hover
                              class="h4 mb-0 mr-25"
                              :title="difficultyTooltip(difficultyFor(roundIndex, themeIndex, questionIndex))"
                          >
                            {{ difficultyFor(roundIndex, themeIndex, questionIndex).difficulty.toFixed(1) }}
                          </span>
                          <span v-else class="h4 mb-0 text-muted mr-25">—</span>
                          <feather-icon
                              v-if="answerFor(roundIndex, themeIndex, questionIndex)"
                              v-b-tooltip.hover
                              :title="answerFor(roundIndex, themeIndex, questionIndex)"
                              icon="HelpCircleIcon"
                              size="12"
                              class="text-muted"
                          />
                        </div>
                      </b-td>
                    </b-tr>
                  </template>
                </b-tbody>
              </b-table-simple>
            </b-card>
          </b-overlay>
        </b-col>
      </b-row>

      <b-row v-else>
        <b-col cols="12">
          <b-overlay
              :show="isLoading"
              rounded="sm"
          >
            <b-card
                title="Данных нет"
                class="col-md-12 col-12"
            >
              <b-card-text>
                Загрузите SIQ-файл для проверки
              </b-card-text>
            </b-card>
          </b-overlay>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BFormGroup,
  BFormSpinbutton,
  BCard,
  BRow,
  BCol,
  BCardText,
  BButton,
  BOverlay,
  BTableSimple,
  BThead,
  BTbody,
  BTr,
  BTh,
  BTd,
  BBadge,
  BLink,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import store from "@/store";
import musicStoreModule from "@/views/db-music/musicStoreModule";
import {onUnmounted, ref, computed} from "@vue/composition-api";
import {useToast} from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {useInputImageRenderer} from "@core/comp-functions/forms/form-utils";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormSpinbutton,
    BCardText,
    BButton,
    BOverlay,
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
    BBadge,
    BLink,
    BSpinner,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  setup() {
    const STORE_MODULE_NAME = 'playlist'
    const toast = useToast()

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const validationData = ref(null)
    const isLoading = ref(false)
    const refInputUploadFile = ref(null)

    const roundsCount = computed(() => (validationData.value && validationData.value.rounds ? validationData.value.rounds.length : 0))
    const itemsCount = computed(() => (validationData.value && validationData.value.items ? validationData.value.items.length : 0))

    // Плитки «Общие параметры пакета» — пока всё нейтрально-зелёное, без порогов
    const summaryStats = computed(() => {
      if (!validationData.value || !validationData.value.summary) return []
      const summary = validationData.value.summary
      return [
        {label: 'Больших раундов', value: summary.rounds_big},
        {label: 'Блиц-раундов', value: summary.rounds_blitz},
        {label: 'Финал', value: summary.final_round_present ? 'Есть' : 'Нет'},
        {label: 'Тем в финале', value: summary.themes_in_final},
        {label: 'Вопросов в теме (Большие)', value: (summary.questions_per_theme_big || []).join(', ') || '—'},
        {label: 'Вопросов в теме (Блиц)', value: (summary.questions_per_theme_blitz || []).join(', ') || '—'},
        {label: 'Аукционов', value: summary.auctions_total},
        {label: 'Котов в мешке', value: summary.cats_total},
      ]
    })

    // === Проверка артистов по Spotify (прогрессивная догрузка + раскраска) ===

    // Реактивная мапа normName(artist.name) -> {loading} | {matched, case_mismatch, spotify, candidates, error}
    const spotifyByName = ref({})
    // Реактивная мапа `артист|название` -> {loading} | {matched, spotify, candidates, error}
    const spotifyTrackByKey = ref({})
    // Счётчик сессии — защита от гонки при повторной загрузке пакета
    const enrichSession = ref(0)
    // Сброшенные ворнинги/бэджи — только в состоянии страницы, никуда не пишем
    const dismissedArtists = ref(new Set())
    const dismissedContent = ref(new Set())
    const dismissedCaseMismatch = ref(new Set())
    const dismissedNotes = ref(new Set())

    // Раскрытие блоков-инструментов: каждый анализ запускается своей кнопкой
    const showCostFormat = ref(false)
    const showArtistsCheck = ref(false)

    // Spotify-догрузка стартует только по кнопке «Исполнители и файлы»,
    // чтобы не жечь запросы, когда нужен только формат или статистика
    const runArtistsCheck = () => {
      showArtistsCheck.value = true
      if (validationData.value && validationData.value.items) {
        enrichSpotify(validationData.value.items)
        enrichSpotifyTracks(validationData.value.items)
      }
    }

    // === Анализ результатов: статистика сыгранных игр с vladimirkhil.com ===

    const statsData = ref(null)
    const statsLoading = ref(false)

    // Минимальный «% правильных ответов» по позиции вопроса в теме (1-я..5-я, дальше — как 5-я):
    // на дешёвом вопросе должны отвечать верно часто, на дорогом — можно реже
    const MIN_CORRECT_BY_POSITION = [80, 70, 60, 45, 30]

    const statsHasData = computed(() => {
      if (!statsData.value) return false
      const topLevel = statsData.value.topLevelStats || {}
      const gamesTotal = (topLevel.startedGameCount || 0) + (topLevel.completedGameCount || 0)
      return gamesTotal > 0 && Object.keys(statsData.value.questionStats || {}).length > 0
    })

    // Позиция вопроса в исходном пакете (0-based) — по ней матчится ключ статистики.
    // theme.question_positions строится бэкендом параллельно prices (без учёта
    // пропущенных нулевых вопросов), поэтому questionIndex (позиция в prices,
    // т.е. по стоимости) и позиция в пакете могут расходиться.
    const questionPosition = (theme, questionIndex) => {
      const positions = theme && theme.question_positions
      const position = positions && positions[questionIndex]
      return position !== undefined ? position : questionIndex
    }

    const statFor = (roundIndex, theme, themeIndex, questionIndex) => {
      const questionStats = statsData.value && statsData.value.questionStats
      const position = questionPosition(theme, questionIndex)
      return (questionStats && questionStats[`${roundIndex}:${themeIndex}:${position}`]) || null
    }

    const takenPercent = stat => (stat && stat.shownCount > 0
      ? Math.round((stat.answeredCount / stat.shownCount) * 100)
      : null)

    // Правильных ответов от показов вопроса (не от нажатий)
    const correctPercent = stat => (stat && stat.shownCount > 0
      ? Math.round((stat.correctCount / stat.shownCount) * 100)
      : null)

    const statCellVariant = (round, roundIndex, theme, themeIndex, questionIndex) => {
      if (round.is_final) return null // у финала нет кнопки — ожидания неприменимы
      const stat = statFor(roundIndex, theme, themeIndex, questionIndex)
      if (!stat || !stat.shownCount) return null
      const correct = correctPercent(stat)
      const threshold = MIN_CORRECT_BY_POSITION[Math.min(questionIndex, MIN_CORRECT_BY_POSITION.length - 1)]
      if (correct >= threshold) return 'success'
      if (correct >= threshold - 20) return 'warning'
      return 'danger'
    }

    // Полупрозрачные цвета Vuexy success/warning/danger — как у table-вариантов
    const STAT_FILL_COLORS = {
      success: 'rgba(40, 199, 111, 0.35)',
      warning: 'rgba(255, 159, 67, 0.35)',
      danger: 'rgba(234, 84, 85, 0.35)',
    }

    // Заливка ячейки: цвет по порогам правильных ответов, ширина заливки = % правильных ответов.
    // При 0% правильных оставляем минимальную полоску, чтобы danger-заливка не исчезала
    const statCellStyle = (round, roundIndex, theme, themeIndex, questionIndex) => {
      const variant = statCellVariant(round, roundIndex, theme, themeIndex, questionIndex)
      if (!variant) return null
      const correct = Math.max(correctPercent(statFor(roundIndex, theme, themeIndex, questionIndex)) || 0, 8)
      const color = STAT_FILL_COLORS[variant]
      return {background: `linear-gradient(to right, ${color} ${correct}%, transparent ${correct}%)`}
    }

    const maxQuestionsInRound = round => round.themes.reduce(
      (maxCount, theme) => Math.max(maxCount, theme.prices.length),
      0,
    )

    // Ответ на вопрос для всплывашки в ячейке анализа.
    // items.question_index — 1-based среди учтённых (ненулевых) вопросов,
    // как и questionIndex по prices, поэтому матчится через +1.
    const answerFor = (roundIndex, themeIndex, questionIndex) => {
      const items = (validationData.value && validationData.value.items) || []
      const item = items.find(candidate => candidate.round_index === roundIndex
        && candidate.theme_index === themeIndex
        && candidate.question_index === questionIndex + 1)
      return (item && item.answer_raw) || null
    }

    // Спец-тип вопроса ('auction' — ставка, 'cat' — секрет) для маркировки ячейки
    const specialTypeFor = (roundIndex, themeIndex, questionIndex) => {
      const items = (validationData.value && validationData.value.items) || []
      const item = items.find(candidate => candidate.round_index === roundIndex
        && candidate.theme_index === themeIndex
        && candidate.question_index === questionIndex + 1)
      return (item && item.special_type) || null
    }

    const specialCellClass = (roundIndex, themeIndex, questionIndex) => ({
      auction: 'cell-special-auction',
      cat: 'cell-special-cat',
    }[specialTypeFor(roundIndex, themeIndex, questionIndex)] || null)

    const specialCellTitle = (roundIndex, themeIndex, questionIndex) => ({
      auction: 'Вопрос со ставкой',
      cat: 'Вопрос с секретом',
    }[specialTypeFor(roundIndex, themeIndex, questionIndex)] || '')

    const loadPackageStats = async () => {
      statsLoading.value = true
      statsData.value = null
      try {
        const res = await store.dispatch('playlist/sigamePackageStats', {
          name: validationData.value.title,
          authors: validationData.value.author,
        })
        statsData.value = res.data
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки статистики',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        statsLoading.value = false
      }
    }

    // === Проверка сложности по БД: формула генератора «артист + трек» ===

    // Мапа "раунд:тема:вопрос" (0-based видимые индексы) -> {difficulty, parts: [...], artist_ids: [...]}
    const difficultyData = ref(null)
    const difficultyLoading = ref(false)

    // Сложность пакета из файла (@difficulty, дефолт 5) — редактируется на форме,
    // подсветка пересчитывается реактивно без повторного запроса
    const packDifficulty = ref(5)

    // Целевые сложности и допуски подсветки
    const FLAT_TARGET_DIFFICULTY = 5
    const RAMP_TARGET_MIN = 3
    const RAMP_TARGET_MAX = 7
    const RAMP_GREEN_TOLERANCE = 1.5
    const RAMP_YELLOW_TOLERANCE = 2.5
    // У плоских тем допуски шире — все вопросы равнозначны, разброс естественен
    const FLAT_GREEN_TOLERANCE = 2.5
    const FLAT_YELLOW_TOLERANCE = 3.5
    const TARGET_SHIFT_PER_DIFFICULTY_POINT = 0.5

    // Тема плоская: есть ненулевые цены и все они равны
    const isFlatTheme = theme => {
      const nonzeroPrices = (theme.prices || []).filter(price => price !== 0)
      return nonzeroPrices.length > 0 && new Set(nonzeroPrices).size === 1
    }

    const clampDifficulty = value => Math.max(1, Math.min(10, value))

    // Целевая сложность ячейки: плоская тема — 5, возрастающая — линейная растяжка 3→7
    // по позиции вопроса; поправка пакета: каждый балл от дефолтных 5 сдвигает цель на 0.5
    const targetDifficultyFor = (theme, questionIndex) => {
      const questionCount = theme.prices.length
      const base = (isFlatTheme(theme) || questionCount < 2)
        ? FLAT_TARGET_DIFFICULTY
        : RAMP_TARGET_MIN + (RAMP_TARGET_MAX - RAMP_TARGET_MIN) * (questionIndex / (questionCount - 1))
      const rawPackDifficulty = Number(packDifficulty.value)
      const packValue = rawPackDifficulty >= 1 && rawPackDifficulty <= 10 ? rawPackDifficulty : 5
      return clampDifficulty(base + (packValue - 5) * TARGET_SHIFT_PER_DIFFICULTY_POINT)
    }

    const difficultyFor = (roundIndex, themeIndex, questionIndex) => {
      const byCell = difficultyData.value
      return (byCell && byCell[`${roundIndex}:${themeIndex}:${questionIndex}`]) || null
    }

    // Раскраска по отклонению от цели; финал и пустые ячейки не красим.
    // Плоские темы (и темы из одного вопроса — у них та же цель 5) — по широким допускам.
    const difficultyCellVariant = (round, roundIndex, theme, themeIndex, questionIndex) => {
      if (round.is_final) return null
      // Секреты не обсчитываем: вопрос достаётся другому игроку, цена ячейки нерелевантна.
      // Заливка светло-фиолетовая (primary), в полоске-саммари считаются зелёными.
      if (specialTypeFor(roundIndex, themeIndex, questionIndex) === 'cat') return 'primary'
      const entry = difficultyFor(roundIndex, themeIndex, questionIndex)
      if (!entry || entry.difficulty === null) return null
      const flat = isFlatTheme(theme) || theme.prices.length < 2
      const greenTolerance = flat ? FLAT_GREEN_TOLERANCE : RAMP_GREEN_TOLERANCE
      const yellowTolerance = flat ? FLAT_YELLOW_TOLERANCE : RAMP_YELLOW_TOLERANCE
      const deviation = Math.abs(entry.difficulty - targetDifficultyFor(theme, questionIndex))
      if (deviation <= greenTolerance) return 'success'
      if (deviation <= yellowTolerance) return 'warning'
      return 'danger'
    }

    // Саммари подсветки для полоски над таблицей: счётчики и доли по цветам,
    // пропущенные (без заливки — финал, нет сложности) — серым сегментом.
    // Считает через difficultyCellVariant, поэтому реактивно и к packDifficulty.
    const difficultySummary = computed(() => {
      const counts = {success: 0, warning: 0, danger: 0, secondary: 0}
      if (difficultyData.value && validationData.value) {
        validationData.value.rounds.forEach((round, roundIndex) => {
          round.themes.forEach((theme, themeIndex) => {
            theme.prices.forEach((price, questionIndex) => {
              const variant = difficultyCellVariant(round, roundIndex, theme, themeIndex, questionIndex)
              // Секреты (primary) в саммари идут в зачёт зелёным
              counts[variant === 'primary' ? 'success' : (variant || 'secondary')] += 1
            })
          })
        })
      }
      const total = counts.success + counts.warning + counts.danger + counts.secondary
      const segments = ['success', 'warning', 'danger', 'secondary']
        .filter(variant => counts[variant] > 0)
        .map(variant => ({
          variant,
          count: counts[variant],
          percent: Math.round((counts[variant] / total) * 100),
          width: ((counts[variant] / total) * 100).toFixed(1),
        }))
      return {total, segments}
    })

    // Тултип сложности: разбивка по частям ответа, части через ' | '
    const difficultyTooltip = entry => ((entry && entry.parts) || [])
      .map(part => {
        const artistPart = part.artist_difficulty !== null ? part.artist_difficulty.toFixed(1) : '—'
        const trackPart = part.track_difficulty !== null ? part.track_difficulty.toFixed(1) : '—'
        return `Артист: ${artistPart} · Трек: ${trackPart}`
      })
      .join(' | ')

    // Уникальные db_id всех артистов вопроса — для ссылок в ячейке сложности
    const itemArtistIds = item => Array.from(new Set(
      (item.artists || []).map(artist => artist.db_id).filter(Boolean),
    ))

    // Уникальные track_id найденных треков по частям ответа эндпоинта
    const entryTrackIds = entry => Array.from(new Set(
      ((entry && entry.parts) || []).map(part => part.track_id).filter(Boolean),
    ))

    const loadDifficulty = async () => {
      const items = (validationData.value && validationData.value.items) || []
      if (!items.length) return
      difficultyLoading.value = true
      difficultyData.value = null
      try {
        // Части ответа: artist_id — первый (основной) артист части, фиты не участвуют
        const payload = items.map(item => ({
          parts: (item.parts || []).map(part => ({
            artist_id: (part.artists[0] && part.artists[0].db_id) || null,
            track_title: part.track_title || null,
          })),
        }))
        const res = await store.dispatch('playlist/sigameQuestionDifficulty', {items: payload})
        const byCell = {}
        items.forEach((item, itemIndex) => {
          // artist_ids кладём из items — ссылки на всех артистов вопроса (включая фитов)
          byCell[`${item.round_index}:${item.theme_index}:${item.question_index - 1}`] = res.data[itemIndex]
            ? {...res.data[itemIndex], artist_ids: itemArtistIds(item)}
            : null
        })
        difficultyData.value = byCell
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка расчёта сложности',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        difficultyLoading.value = false
      }
    }

    const normName = name => (name || '').trim().replace(/\s+/g, ' ').toLowerCase()

    const artistSpotify = artist => spotifyByName.value[normName(artist.name)] || null

    const artistState = artist => {
      const sp = artistSpotify(artist)
      if (!sp || sp.loading) return 'loading'
      if (sp.matched === true) return 'ok'
      // В БД есть привязка к Spotify — доверяем связке, помечаем зелёным
      if (artist.db_id && artist.db_spotify_id) return 'ok'
      return artist.db_id ? 'warning' : 'bad'
    }

    const spotifyArtistUrl = id => `https://open.spotify.com/artist/${id}`

    const artistClass = artist => ({
      loading: 'text-muted',
      ok: 'text-success font-weight-bold',
      warning: 'text-warning font-weight-bold',
      bad: 'text-danger font-weight-bold',
    }[artistState(artist)])

    const caseMismatch = artist => {
      const sp = artistSpotify(artist)
      return !!(sp && sp.case_mismatch)
    }

    const artistKey = (itemIndex, artistIndex) => `${itemIndex}:${artistIndex}`

    const dismissArtist = key => {
      const next = new Set(dismissedArtists.value)
      next.add(key)
      dismissedArtists.value = next
    }

    // Бэдж «регистр отличается» — показываем, пока не сброшен галочкой
    const showCaseMismatch = (artist, itemIndex, artistIndex) => caseMismatch(artist)
      && !dismissedCaseMismatch.value.has(artistKey(itemIndex, artistIndex))

    const dismissCaseMismatch = key => {
      const next = new Set(dismissedCaseMismatch.value)
      next.add(key)
      dismissedCaseMismatch.value = next
    }

    // Бэдж-заметка «зелёный из-за привязки в БД»: показываем только если в БД есть
    // Spotify-привязка И имя НЕ совпало по Spotify (иначе совпадение и так green), пока не сброшен галочкой
    const showNote = (artist, itemIndex, artistIndex) => {
      const sp = artistSpotify(artist)
      return !!(artist.db_id && artist.db_spotify_id && sp && !sp.loading && sp.matched !== true)
        && !dismissedNotes.value.has(artistKey(itemIndex, artistIndex))
    }

    const dismissNote = key => {
      const next = new Set(dismissedNotes.value)
      next.add(key)
      dismissedNotes.value = next
    }

    const rowIsRed = item => !item.answer_raw || item.artists.length === 0

    const contentIcon = kind => ({image: '🖼', audio: '🎵', video: '🎬', text: '📝', none: ''}[kind] || '')

    // Ворнинги считаются по каждому файлу вопроса отдельно, ключ сброса — «вопрос:файл»
    const contentKey = (itemIndex, fileIndex) => `${itemIndex}:${fileIndex}`

    const fileHasWarning = (content, itemIndex, fileIndex) => (content.warnings || []).length > 0
        && !dismissedContent.value.has(contentKey(itemIndex, fileIndex))

    const fileClass = (content, itemIndex, fileIndex) => (fileHasWarning(content, itemIndex, fileIndex) ? 'text-danger font-weight-bold' : '')

    // Человекочитаемые причины контент-ворнинга
    const CONTENT_WARNING_LABELS = {size: 'Больше 200 КБ', png: 'Формат PNG', webp: 'Формат WEBP'}
    const contentWarningReasons = content => (content.warnings || []).map(code => CONTENT_WARNING_LABELS[code] || code)

    // === Проверка наличия песни в Spotify ===

    // Строки колонки «Трек»: по одной на часть ответа с названием,
    // артист для проверки — первый в своей части
    const trackRows = item => (item.parts || [])
        .filter(part => part.track_title)
        .map(part => ({
          track: part.track_title,
          artist: (part.artists && part.artists[0] && part.artists[0].name) || null,
        }))

    const trackKey = row => `${normName(row.artist)}|${normName(row.track)}`

    const trackSpotify = row => spotifyTrackByKey.value[trackKey(row)] || null

    const trackState = row => {
      const found = trackSpotify(row)
      if (!found) return 'idle'
      if (found.loading) return 'loading'
      return found.matched ? 'ok' : 'bad'
    }

    const spotifyTrackUrl = id => `https://open.spotify.com/track/${id}`

    // Формат стоимостей
    const roundCategoryLabel = category => ({big: 'Большой', blitz: 'Блиц', final: 'Финал'}[category] || category)
    const roundCategoryVariant = category => ({big: 'light-primary', blitz: 'light-warning', final: 'light-secondary'}[category] || 'light-secondary')
    const costRowVariant = status => ({error: 'danger', warning: 'warning'}[status] || null)

    const dismissContent = key => {
      const next = new Set(dismissedContent.value)
      next.add(key)
      dismissedContent.value = next
    }

    // Прогрессивная догрузка Spotify: уникальные артисты, чанки по 15, последовательно
    function enrichSpotify(items) {
      const session = enrichSession.value
      const uniqueMap = new Map()
      items.forEach(item => {
        (item.artists || []).forEach(artist => {
          const key = normName(artist.name)
          if (key && !uniqueMap.has(key)) {
            uniqueMap.set(key, {name: artist.name, spotify_id: artist.db_spotify_id || undefined})
          }
        })
      })
      const uniqueArtists = Array.from(uniqueMap.values())
      if (!uniqueArtists.length) return

      // Помечаем всех как loading
      const loadingPatch = {}
      uniqueArtists.forEach(a => {
        loadingPatch[normName(a.name)] = {loading: true}
      })
      spotifyByName.value = {...spotifyByName.value, ...loadingPatch}

      // Разбиваем на чанки по 15
      const chunkSize = 15
      const chunks = []
      for (let i = 0; i < uniqueArtists.length; i += chunkSize) {
        chunks.push(uniqueArtists.slice(i, i + chunkSize))
      }

      // Обрабатываем чанки последовательно (reduce-цепочка промисов)
      chunks.reduce((chain, chunk) => chain.then(() => {
        if (session !== enrichSession.value) return undefined // устаревшая загрузка — не пишем
        return store.dispatch('playlist/spotifyValidateArtists', {artists: chunk})
            .then(res => {
              if (session !== enrichSession.value) return
              const patch = {}
              ;(res.data || []).forEach(entry => {
                patch[normName(entry.name)] = {loading: false, ...entry}
              })
              spotifyByName.value = {...spotifyByName.value, ...patch}
            })
            .catch(() => {
              if (session !== enrichSession.value) return
              const patch = {}
              chunk.forEach(a => {
                patch[normName(a.name)] = {loading: false, error: true}
              })
              spotifyByName.value = {...spotifyByName.value, ...patch}
            })
      }), Promise.resolve())
    }

    // Прогрессивная проверка песен: уникальные пары «артист — название», чанки по 10.
    // Ответ раскладываем по индексу чанка — бэкенд чистит кавычки, и имя может отличаться.
    function enrichSpotifyTracks(items) {
      const session = enrichSession.value
      const uniqueMap = new Map()
      items.forEach(item => {
        trackRows(item).forEach(row => {
          if (!row.artist) return
          const key = trackKey(row)
          if (!uniqueMap.has(key)) uniqueMap.set(key, {artist: row.artist, track: row.track})
        })
      })
      const pairs = Array.from(uniqueMap.values())
      if (!pairs.length) return

      // Помечаем все пары как loading
      const loadingPatch = {}
      pairs.forEach(pair => {
        loadingPatch[trackKey(pair)] = {loading: true}
      })
      spotifyTrackByKey.value = {...spotifyTrackByKey.value, ...loadingPatch}

      const chunkSize = 10
      const chunks = []
      for (let i = 0; i < pairs.length; i += chunkSize) {
        chunks.push(pairs.slice(i, i + chunkSize))
      }

      chunks.reduce((chain, chunk) => chain.then(() => {
        if (session !== enrichSession.value) return undefined // устаревшая загрузка — не пишем
        return store.dispatch('playlist/spotifyValidateTracks', {pairs: chunk})
            .then(res => {
              if (session !== enrichSession.value) return
              const patch = {}
              ;(res.data || []).forEach((entry, entryIndex) => {
                const pair = chunk[entryIndex]
                if (!pair) return
                patch[trackKey(pair)] = {loading: false, ...entry}
              })
              spotifyTrackByKey.value = {...spotifyTrackByKey.value, ...patch}
            })
            .catch(() => {
              if (session !== enrichSession.value) return
              const patch = {}
              chunk.forEach(pair => {
                patch[trackKey(pair)] = {loading: false, error: true}
              })
              spotifyTrackByKey.value = {...spotifyTrackByKey.value, ...patch}
            })
      }), Promise.resolve())
    }

    const {inputImageRenderer: inputSiqFileRenderer} = useInputImageRenderer(refInputUploadFile, async base64 => {
      const file = refInputUploadFile.value.files[0]
      enrichSession.value += 1
      isLoading.value = true
      validationData.value = null
      spotifyByName.value = {}
      spotifyTrackByKey.value = {}
      statsData.value = null
      difficultyData.value = null
      showCostFormat.value = false
      showArtistsCheck.value = false
      dismissedArtists.value = new Set()
      dismissedContent.value = new Set()
      dismissedCaseMismatch.value = new Set()
      dismissedNotes.value = new Set()
      try {
        const res = await store.dispatch('playlist/sigameValidate', {file: file})
        validationData.value = res.data
        packDifficulty.value = (res.data && res.data.difficulty) || 5
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки файла',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        isLoading.value = false
      }
    })

    return {
      validationData,
      isLoading,
      inputSiqFileRenderer,
      refInputUploadFile,
      roundsCount,
      itemsCount,
      summaryStats,
      spotifyByName,
      dismissedArtists,
      dismissedContent,
      artistSpotify,
      artistState,
      artistClass,
      caseMismatch,
      artistKey,
      dismissArtist,
      spotifyArtistUrl,
      showCaseMismatch,
      dismissCaseMismatch,
      showNote,
      dismissNote,
      rowIsRed,
      contentIcon,
      contentKey,
      fileHasWarning,
      fileClass,
      contentWarningReasons,
      trackRows,
      trackSpotify,
      trackState,
      spotifyTrackUrl,
      dismissContent,
      roundCategoryLabel,
      roundCategoryVariant,
      costRowVariant,
      statsData,
      statsLoading,
      statsHasData,
      statFor,
      takenPercent,
      correctPercent,
      statCellStyle,
      maxQuestionsInRound,
      answerFor,
      specialCellClass,
      specialCellTitle,
      loadPackageStats,
      difficultyData,
      difficultyLoading,
      packDifficulty,
      difficultyFor,
      difficultyCellVariant,
      difficultySummary,
      difficultyTooltip,
      itemArtistIds,
      entryTrackIds,
      loadDifficulty,
      showCostFormat,
      showArtistsCheck,
      runArtistsCheck,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-blog.scss';

/* Маркировка спец-вопросов в таблицах сложности и анализа результатов:
   рамка не конфликтует с заливкой статусов (danger/warning/success) */
.cell-special-auction {
  box-shadow: inset 0 0 0 2px #1e88e5; /* ставка — синяя */
}
.cell-special-cat {
  box-shadow: inset 0 0 0 2px #7367f0; /* секрет — фиолетовая */
}
/* Светло-фиолетовая заливка секретов (variant="primary" на td) в таблице
   сложности: тема может не прокрашивать table-primary — задаём фон явно */
.cws-container td.table-primary {
  background-color: rgba(115, 103, 240, 0.18);
}
</style>
